import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { BirthdayStudent, birthdayWhenLabel } from '../utils/birthdays';

interface Props {
  students: BirthdayStudent[];
  /** Optional click handler (e.g. navigate to the students list). */
  onClick?: () => void;
}

/** Show the collapse toggle once the list gets long enough to take real space. */
const COLLAPSE_THRESHOLD = 5;

/**
 * 🎂 banner listing students with a birthday today or in the upcoming window.
 * Renders nothing when there are no upcoming birthdays. Used by both the admin
 * Overview and the staff (teacher) Overview.
 *
 * When there are {@link COLLAPSE_THRESHOLD}+ students the list collapses by
 * default to save vertical space; a chevron toggles it open.
 */
export function BirthdayBanner({ students, onClick }: Props) {
  const collapsible = students.length >= COLLAPSE_THRESHOLD;
  // Long lists start collapsed so they don't dominate the dashboard.
  const [expanded, setExpanded] = useState(!collapsible);

  if (students.length === 0) return null;

  const hasToday = students.some((s) => s.daysAway === 0);
  const todayCount = students.filter((s) => s.daysAway === 0).length;
  const title = hasToday ? 'Birthdays' : 'Upcoming birthdays';
  const showList = !collapsible || expanded;

  return (
    <div
      className={`bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 shadow rounded-lg p-4 transition-shadow duration-200${
        onClick ? ' cursor-pointer hover:shadow-md' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-start">
        <span className="text-2xl mr-3" role="img" aria-label="Birthday">🎂</span>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-pink-800">
              {title}
              {collapsible && (
                <span className="ml-1 font-normal text-pink-600">
                  ({students.length} {students.length === 1 ? 'student' : 'students'})
                </span>
              )}
            </div>
            {collapsible && (
              <button
                type="button"
                onClick={(e) => {
                  // Don't trigger the banner's navigate handler.
                  e.stopPropagation();
                  setExpanded((prev) => !prev);
                }}
                className="flex items-center gap-1 text-xs font-medium text-pink-700 hover:text-pink-900"
                aria-expanded={expanded}
              >
                {expanded ? (
                  <>
                    Hide <ChevronDown className="w-3.5 h-3.5" />
                  </>
                ) : (
                  <>
                    Show <ChevronRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            )}
          </div>
          {showList ? (
            <ul className="space-y-0.5 mt-1">
              {students.map((s) => (
                <li key={s.id} className="text-sm text-gray-700">
                  <span className="font-medium text-gray-900">{s.name}</span>
                  {s.turnsAge !== null && <> turns {s.turnsAge}</>}
                  {' — '}
                  <span
                    className={
                      s.daysAway === 0 ? 'font-semibold text-pink-700' : 'text-gray-500'
                    }
                  >
                    {birthdayWhenLabel(s)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-500 mt-1">
              {todayCount > 0
                ? `${todayCount} ${todayCount === 1 ? 'birthday' : 'birthdays'} today`
                : `Next: ${students[0].name} — ${birthdayWhenLabel(students[0])}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
